import React, { useState } from 'react';
import { Button, Modal, Checkbox, Row, Col, App, Flex, ButtonProps } from 'antd';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import convertTo2DArray from '@/utils/convert2double';

export interface ExportColumn {
  key: string;
  label: string;
}

export interface ExportExcelButtonProps<T, TContext = any> extends ButtonProps {
  list?: Array<T>;
  isAll?: boolean;
  loadData?: () => Promise<T[]>;
  columns: ExportColumn[] | ((t: any) => ExportColumn[]);
  defaultSelectedColumns?: string[] | ((t: any) => string[]);
  dataMapper: (item: T, t: any, context?: TContext) => Record<string, string | number | undefined>;
  fileName?: string;
  sheetName?: string;
  context?: TContext;
  btnText?: string;
  colQty?: number;
}

function ExportExcelButton<T, TContext = any>({
  list = [],
  isAll = false,
  loadData,
  columns,
  defaultSelectedColumns,
  dataMapper,
  fileName = 'export.xlsx',
  sheetName = 'Sheet1',
  context,
  btnText,
  colQty = 7,
  ...resProps
}: ExportExcelButtonProps<T, TContext>) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const columnsList = typeof columns === 'function' ? columns(t) : columns;
  const allColumnKeys = columnsList.map(col => col.key);

  const getDefaultColumnKeys = () => {
    if (defaultSelectedColumns) {
      const values = typeof defaultSelectedColumns === 'function'
        ? defaultSelectedColumns(t)
        : defaultSelectedColumns;
      // 兼容调用方传入稳定 key 或翻译后的 label，内部统一保存 key
      return values.map(value => {
        const col = columnsList.find(c => c.key === value || c.label === value);
        return col?.key;
      }).filter((key): key is string => Boolean(key));
    }
    return allColumnKeys.slice(0, 3);
  };

  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>(getDefaultColumnKeys());

  const handleOk = async () => {

    if (selectedColumnKeys.length === 0) {
      Modal.warning({
        title: t('common.warning'),
        content: t('common.pleaseSelectColumns'),
      });
      return;
    }

    if (isAll && loadData) {
      setVisible(false);
      setLoading(true);
      try {
        const allData = await loadData();
        exportToExcel(selectedColumnKeys, allData);
      } catch (error) {
        console.error(`${t('common.exportErr')}:`, error);
        notification.error({
          placement: 'top',
          message: t('common.exportErr'),
          description: error instanceof Error ? error.message : t('common.serverError'),
        });
      } finally {
        setLoading(false);
      }
    } else {
      setVisible(false);
      exportToExcel(selectedColumnKeys);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleCheckboxChange = (checkedValues: string[]) => {
    setSelectedColumnKeys(checkedValues);
  };

  const exportToExcel = (selectedKeys: string[], dataList?: T[]) => {
    const exportList = dataList || list;
    const selectedColumns = selectedKeys
      .map(key => columnsList.find(column => column.key === key))
      .filter((column): column is ExportColumn => Boolean(column));
    const data = exportList.map((item) => {
      const initData = dataMapper(item, t, context);
      const row: Record<string, string | number | undefined> = {};
      selectedColumns.forEach((column) => {
        row[column.label] = initData[column.label];
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, fileName);
  };

  return (
    <>
      <Button onClick={() => setVisible(true)} {...resProps}>
        {btnText ? btnText : isAll ? t('common.exportAllExcel') : t('common.exportCurrentPage')}
      </Button>
      <Modal
        title={t('sale.selectColumns')}
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={'60vw'}
        confirmLoading={loading}
      >
        <Flex vertical style={{ width: "100%" }}>
          <div style={{ marginTop: 15, marginBottom: 10, width: "100%" }}>
            <Checkbox
              indeterminate={selectedColumnKeys.length > 0 && selectedColumnKeys.length < allColumnKeys.length}
              checked={selectedColumnKeys.length === allColumnKeys.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedColumnKeys(allColumnKeys);
                } else {
                  setSelectedColumnKeys([]);
                }
              }}
            >
              {t('common.selectAll')}
            </Checkbox>
          </div>
          <Checkbox.Group
            style={{ width: "100%" }}
            value={selectedColumnKeys}
            onChange={handleCheckboxChange}
          >
            <Flex justify="space-between" style={{ width: "100%" }}>
              {
                convertTo2DArray(columnsList, colQty)
                  .map((datas, cols) => (
                    <div>
                      {
                        datas.map((column, index) => {
                          return (
                            <Col key={`${cols}-${index}`} style={{ marginBottom: 4 }}>
                              <Checkbox value={column.key}>{column.label}</Checkbox>
                            </Col>
                          )
                        })
                      }
                    </div>
                  ))
              }
            </Flex>
          </Checkbox.Group>
        </Flex>
      </Modal>
    </>
  );
}

export default ExportExcelButton;
